import React from 'react';
import * as Sentry from '@sentry/react';

const RELOAD_FLAG_KEY = 'chunk-error-reloaded';

const isChunkLoadError = error =>
  /Loading chunk .* failed/i.test(error?.message || '') ||
  /Failed to fetch dynamically imported module/i.test(error?.message || '') ||
  error?.name === 'ChunkLoadError';

/**
 * Depois de um deploy, uma aba já aberta referencia chunks (por hash) que somem do
 * servidor assim que o build novo sobe — o próximo import() dinâmico (troca de rota)
 * rejeita e derruba a árvore inteira sem boundary nenhum. Aqui: detecta esse erro
 * específico e recarrega a página UMA vez (sessionStorage evita loop se o erro persistir
 * por outro motivo); qualquer outro erro cai num fallback simples, sem mascarar bugs reais.
 */
class ChunkErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, isChunkError: false };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, isChunkError: isChunkLoadError(error) };
  }

  componentDidMount() {
    window.sessionStorage.removeItem(RELOAD_FLAG_KEY);
  }

  componentDidCatch(error, info) {
    if (!this.state.isChunkError) {
      // Sem isso, um erro real de render (não relacionado a chunk) fica só no state do
      // boundary — sem stack nenhum visível pra diagnosticar o que quebrou.
      console.error('ChunkErrorBoundary capturou um erro (não é chunk stale):', error, info?.componentStack);
      Sentry.captureException(error, { contexts: { react: { componentStack: info?.componentStack } } });
      return;
    }
    const alreadyReloaded = window.sessionStorage.getItem(RELOAD_FLAG_KEY);
    if (alreadyReloaded) return;
    window.sessionStorage.setItem(RELOAD_FLAG_KEY, '1');
    window.location.reload();
  }

  render() {
    if (this.state.hasError) {
      if (this.state.isChunkError) return null; // reload já disparado — evita flash de UI quebrada
      return (
        <div style={{ padding: 40, textAlign: 'center' }}>
          <p>Algo deu errado. Tente recarregar a página.</p>
          <button onClick={() => window.location.reload()}>Recarregar página</button>
        </div>
      );
    }
    return this.props.children;
  }
}

export { ChunkErrorBoundary };
